// ---------------------------------------------------------------------------
// Payload QR Corrigeo — encodage/décodage 100 % côté client.
//
// Le QR encode une URL `${origin}/qr#<payload>` où <payload> est le JSON
// { t: feedback, n: note ("14/20", optionnel), e: prénom (optionnel),
//   d: date ISO courte, a: id d'enregistrement vocal (optionnel) }
// encodé en UTF-8 puis en base64url (sans padding).
//
// Le payload texte voyage dans le FRAGMENT (#) de l'URL : il n'est JAMAIS
// envoyé au serveur — le téléphone de l'élève décode tout localement sur la
// page /qr (RGPD). Seul `a` référence un fichier audio (voix du professeur)
// hébergé sur educform31.fr, purgé automatiquement après 180 jours.
// ---------------------------------------------------------------------------

// Un QR code reste lisible tant que la charge est modeste. On plafonne le
// feedback encodé pour garantir un scan fiable sur téléphone, même en
// impression noir & blanc. Au-delà, on tronque proprement à la phrase près.
export const QR_MAX_CHARS = 600;

/** Tronque un texte à la dernière phrase complète sous `max` caractères. */
export function smartTruncate(text, max = QR_MAX_CHARS) {
  const clean = (text || "").replace(/\s+/g, " ").trim();
  if (clean.length <= max) return { text: clean, truncated: false };
  const suffix = " … (feedback complet donné en classe)";
  const budget = max - suffix.length;
  let slice = clean.slice(0, budget);
  // On recule jusqu'à une fin de phrase (. ! ?) pour couper proprement.
  const lastStop = Math.max(
    slice.lastIndexOf(". "),
    slice.lastIndexOf("! "),
    slice.lastIndexOf("? ")
  );
  if (lastStop > budget * 0.5) {
    slice = slice.slice(0, lastStop + 1);
  } else {
    // Pas de phrase exploitable : on coupe au dernier espace pour ne pas
    // trancher un mot en deux.
    const lastSpace = slice.lastIndexOf(" ");
    if (lastSpace > 0) slice = slice.slice(0, lastSpace);
  }
  return { text: slice.trim() + suffix, truncated: true };
}

// Identifiant de fichier voix : nom de fichier simple (lettres, chiffres,
// point, tiret, underscore) — jamais de « / » ni de caractère exotique.
const VOICE_ID_RE = /^[A-Za-z0-9][A-Za-z0-9._-]{0,79}$/;

/** Date ISO courte du jour (AAAA-MM-JJ). */
export function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

/** "2026-07-02" → "02/07/2026" (affichage français, tolérant). */
export function formatDateFr(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso || "";
}

/**
 * Encode le payload en base64url (UTF-8, sans padding).
 * Champs vides omis pour garder le QR le plus léger possible.
 */
export function encodeQrPayload({ t, n, e, d, a }) {
  const payload = { t: (t || "").trim() };
  if (n && String(n).trim()) payload.n = String(n).trim();
  if (e && String(e).trim()) payload.e = String(e).trim();
  payload.d = (d && String(d).trim()) || todayIso();
  // `a` : identifiant du fichier voix (ex : "ab12cd.webm") renvoyé par
  // audio-upload.php — uniquement des caractères sûrs pour l'URL.
  if (a && VOICE_ID_RE.test(String(a).trim())) payload.a = String(a).trim();
  const bytes = new TextEncoder().encode(JSON.stringify(payload));
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

/** URL complète à encoder dans le QR : `${origin}/qr#<payload>`. */
export function buildQrUrl(fields) {
  return `${window.location.origin}/qr#${encodeQrPayload(fields)}`;
}

/**
 * Décode un fragment d'URL (avec ou sans « # » initial).
 * Retourne { t, n, e, d, a } ou `null` si absent/corrompu — jamais d'exception.
 */
export function decodeQrPayload(fragment) {
  try {
    const raw = (fragment || "").replace(/^#/, "").trim();
    if (!raw) return null;
    const b64 = raw.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const bin = atob(padded);
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    const obj = JSON.parse(new TextDecoder("utf-8").decode(bytes));
    if (!obj || typeof obj.t !== "string" || !obj.t.trim()) return null;
    const str = (v) => (typeof v === "string" && v.trim() ? v.trim() : null);
    const aRaw = str(obj.a);
    return {
      t: obj.t.trim(),
      n: str(obj.n),
      e: str(obj.e),
      d: str(obj.d),
      // Id voix validé strictement (sinon ignoré) : jamais utilisé tel quel
      // pour construire un chemin serveur sans ce filtre.
      a: aRaw && VOICE_ID_RE.test(aRaw) ? aRaw : null,
    };
  } catch {
    return null;
  }
}
